import { createSequence } from '@olivierpascal/helpers';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import {
  IapPollVoteListItem,
  IapPollVoteListItemSkeleton,
} from '~/components/molecules/IapPollVoteListItem';
import { Pagination } from '~/components/molecules/Pagination';
import { Table } from '~/components/molecules/Table';
import { usePagination } from '~/hooks/usePagination';
import { useIapPollResultsQuery } from '~/lib/api/modules/IapPolls/hooks/useIapPollResultsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(3, 1fr)';
const DEFAULT_SKELETON_COUNT = 4;

export type IIapPollVoteListProps = IContainerProps & {
  appId: string;
  iapPollId: string;
};

export const IapPollVoteList: React.FC<IIapPollVoteListProps> = (props) => {
  const { sx, appId, iapPollId } = props;
  const pagination = usePagination();
  const iapPollResultsQuery = useIapPollResultsQuery(
    {
      appId,
      iapPollId,
      body: {
        start: pagination.start,
        limit: pagination.limit,
      },
    },
    {
      enabled:
        Number.isInteger(pagination.start) &&
        Number.isInteger(pagination.limit),
      keepPreviousData: true,
    },
  );

  const iapPollResults = iapPollResultsQuery.data;
  const isEmpty =
    !iapPollResultsQuery.isLoading && !iapPollResults?.votes?.length;

  if (isEmpty) {
    return null;
  }

  return (
    <ContentBlock sx={sx}>
      {iapPollResultsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={iapPollResultsQuery.error} />
        </ContentBlock.Body>
      ) : (
        <>
          <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
            {iapPollResultsQuery.isLoading ||
            iapPollResultsQuery.isPreviousData ? (
              createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <IapPollVoteListItemSkeleton key={index} first={index === 0} />
              ))
            ) : (
              <>
                {iapPollResults?.votes?.map(
                  (iapPollVote, iapPollVoteIndex) =>
                    iapPollResults.iapPoll && (
                      <IapPollVoteListItem
                        key={iapPollVote._id}
                        vote={iapPollVote}
                        first={iapPollVoteIndex === 0}
                      />
                    ),
                )}
              </>
            )}
          </Table>

          <ContentBlock.Footer>
            <Pagination
              pagination={pagination}
              total={iapPollResultsQuery.data?.totalVotes}
            />
          </ContentBlock.Footer>
        </>
      )}
    </ContentBlock>
  );
};
